import { useEffect, useState } from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import {
  get_superadmin_blog_post,
  publish_superadmin_blog_post,
  delete_superadmin_blog_post,
  unpublish_superadmin_blog_post,
} from '@/api/blog';

import { handleServerError } from '@/utils/handle-server-error';

type BlogPreview = {
  id: string;
  title: string;
  summary?: string | null;
  content_markdown: string;
  cover_image?: string | null;
  is_published: boolean;
  category?: {
    id: string;
    name: string;
  } | null;
  hashtags?: {
    id: string;
    name: string;
  }[];
  language?: string;
};

export default function BlogPreviewView() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useSearch({
    from: '/_authenticated/super-admin-blog-preview',
  }) as { id?: string };

  const [blog, setBlog] = useState<BlogPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] =
    useState<'publish' | 'delete' | null>(null);

  // ---------------- Fetch blog ----------------
  useEffect(() => {
    if (!id) return;

    const fetchBlog = async () => {
      try {
        const data = await get_superadmin_blog_post(id);
        setBlog(data);
      } catch (error) {
        handleServerError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [id]);

  useEffect(() => {
    if (blog?.language && i18n.language !== blog.language) {
      i18n.changeLanguage(blog.language);
    }
  }, [blog?.language, i18n]);

  // ---------------- Actions ----------------
  const handlePublish = async () => {
    if (!blog) return;
    try {
      setActionLoading('publish');
      await publish_superadmin_blog_post(blog.id);
      navigate({ to: '/super-admin-blog' });
    } catch (error) {
      handleServerError(error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnpublish = async () => {
    if (!blog) return;

    try {
      setActionLoading('publish');
      await unpublish_superadmin_blog_post(blog.id);
      navigate({ to: '/super-admin-blog' });
    } catch (error) {
      handleServerError(error);
    } finally {
      setActionLoading(null);
    }
  };


  const handleDelete = async () => {
    if (!blog) return;
    if (!confirm('Are you sure you want to delete this blog post?')) return;

    try {
      setActionLoading('delete');
      await delete_superadmin_blog_post(blog.id);
      navigate({ to: '/super-admin-blog' });
    } catch (error) {
      handleServerError(error);
    } finally {
      setActionLoading(null);
    }
  };

  // ---------------- UI ----------------
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin h-8 w-8 border-b-2 border-primary rounded-full" />
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="p-6 text-center space-y-4">
        <p className="text-muted-foreground">Nothing to preview</p>
        <Button onClick={() => navigate({ to: '/super-admin-blog' })}>
          Back
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Actions */}
      <div className="flex justify-between flex-wrap gap-3">
        <Button
          variant="outline"
          onClick={() => navigate({ to: '/super-admin-blog' })}
        >
          ← {t('Back_to_blog_menu')}
        </Button>

        <div className="flex gap-2">
          {blog.is_published ? (
            <Button
              variant="secondary"
              disabled={actionLoading === 'publish'}
              onClick={handleUnpublish}
            >
              {actionLoading === 'publish'
                ? t('unpublishing')
                : t('Unpublish')}
            </Button>
          ) : (
            <Button
              disabled={actionLoading === 'publish'}
              onClick={handlePublish}
            >
              {actionLoading === 'publish'
                ? t('publishing')
                : t('Publish')}
            </Button>
          )}


          <Button
            variant="destructive"
            disabled={actionLoading === 'delete'}
            onClick={handleDelete}
          >
            {actionLoading === 'delete' ? 'Deleting...' : t('Delete')}
          </Button>
        </div>
      </div>

      {/* Cover Image */}
      {blog.cover_image ? (
        <img
          src={blog.cover_image}
          alt="Cover"
          className="w-full rounded-xl max-h-[400px] object-cover"
        />
      ) : (
        <div className="w-full rounded-xl bg-gray-100 h-60 flex items-center justify-center text-gray-400">
          No Cover Image
        </div>
      )}

      {/* Meta */}
      <p className="text-sm text-muted-foreground">
        {t('category_b')}: {blog.category?.name ?? 'None'}
      </p>

      <p className="text-sm text-muted-foreground">
        {t('tags')}:{' '}
        {blog.hashtags && blog.hashtags.length > 0
          ? blog.hashtags.map((t) => t.name).join(', ')
          : 'None'}
      </p>

      {/* Content */}
      <Card>
        <CardContent className="pt-6">
          <h1 className="text-3xl font-bold mb-2">{blog.title}</h1>

          {blog.summary && (
            <p className="text-muted-foreground mb-6">{blog.summary}</p>
          )}

          {/* ✅ Markdown Preview */}
          <article className="prose max-w-none dark:prose-invert">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {blog.content_markdown}
            </ReactMarkdown>
          </article>
        </CardContent>
      </Card>
    </div>
  );
}
